import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PageShell } from "../components/Layout";
import { toast } from "sonner";
import { Plus, Sparkles, Search, X as XIcon, Lock } from "lucide-react";
import api from "../lib/api";
import PaywallGate from "../components/PaywallGate";
import PremiumTeaser from "../components/PremiumTeaser";

const ZODIAC_SIGNS = [
  "Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo",
  "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces",
];
const PLANETS = ["Sun", "Moon", "Mars", "Mercury", "Jupiter",
                 "Venus", "Saturn", "Rahu", "Ketu"];
const HOUSES = Array.from({ length: 12 }, (_, i) => i + 1);

// sessionStorage key — one flat blob so restoring after browser Back is
// a single JSON.parse. sessionStorage (not localStorage) so the cache
// dies with the tab; users don't expect stale results next visit.
const CACHE_KEY = "celeb_lookalike_state_v1";

const loadCachedState = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Guard against schema drift — bail if any expected key is missing.
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Celebrity Lookalike Calculator — astro research tool.
 *
 * Input: ascendant zodiac + up to 9 (planet, house, sign) filters. Filters
 * are optional and AND-ed together, so a user can search "Sagittarius
 * lagna + Saturn in Aquarius in 3rd" and get every celeb with that
 * exact combo. Result cards click through to the celebrity detail page.
 *
 * Zero LLM — the endpoint (`POST /celebrities/lookalike`) filters an
 * in-memory signature cache computed at backend startup via Swiss
 * Ephemeris.
 */
export default function CelebrityLookalikePage() {
  // Hydrate from sessionStorage on first render so hitting browser Back
  // after opening a match returns the user to their filled form + results
  // instead of a pristine page. Cached synchronously in useState
  // initialisers so the first paint already shows the previous state.
  const cached = loadCachedState();
  const [asc, setAsc] = useState(cached?.asc || "");
  // Each filter row: {planet, house, sign}. house/sign are strings from
  // the dropdowns; we cast to int for the API. Empty planet = ignored.
  const [filters, setFilters] = useState(
    cached?.filters?.length ? cached.filters : [{ planet: "", house: "", sign: "" }]
  );
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(cached?.result || null); // {count, warming, matches}
  const [error, setError] = useState("");
  // Live-progress state populated from the NDJSON stream. `done/total`
  // drives the bar percentage; `current` is the name being scanned right
  // now (renders as the label under the bar). Reset on every new search.
  const [progress, setProgress] = useState(null); // {done, total, current}

  // Persist form + results (not transient progress/busy) to sessionStorage
  // whenever they change. Using a ref-gated writer instead of a raw effect
  // would over-complicate this — the payload is tiny (< 20 KB even for
  // large result sets) so a JSON.stringify every keystroke is fine.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ asc, filters, result })
      );
    } catch { /* quota exceeded or storage disabled — silently drop */ }
  }, [asc, filters, result]);

  const addFilter = () => {
    if (filters.length >= 9) return; // 9 grahas max — one row per planet.
    setFilters([...filters, { planet: "", house: "", sign: "" }]);
  };
  const updateFilter = (idx, patch) => {
    setFilters(filters.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };
  const removeFilter = (idx) => setFilters(filters.filter((_, i) => i !== idx));

  // Pseudo "birth details" encoding the QUERY — the paywall pipeline
  // fingerprints (tool, birthDetails), so for this tool the chart being
  // unlocked IS the search. MUST stay in lock-step with the backend's
  // `_lookalike_query_birth` (server.py).
  const activeFilters = filters
    .filter((f) => f.planet && (f.house || f.sign))
    .map((f) => ({
      planet: f.planet,
      house: f.house ? parseInt(f.house, 10) : null,
      sign: f.sign || null,
    }));
  const canonFilters = activeFilters
    .map((f) => `${f.planet}:${f.house ?? ""}:${f.sign ?? ""}`)
    .sort()
    .join(",");
  const queryBirth = {
    date: (asc || "any").trim(),
    time: (canonFilters || "any").trim(),
    place: "lookalike",
    timezone: "",
  };

  // After unlock (fresh payment OR hydration with a prior unlock), re-fetch
  // the full match list — the backend now returns every match since the
  // query fingerprint is unlocked for this user.
  const fetchFullResults = async () => {
    try {
      const { data } = await api.post("/celebrities/lookalike", {
        ascendant: asc || null,
        planet_filters: activeFilters,
      });
      setResult(data);
    } catch { /* keep current results on transient failure */ }
  };

  const runSearch = async () => {
    setError("");
    setResult(null);
    setProgress(null);
    const active = filters
      .filter((f) => f.planet && (f.house || f.sign))
      .map((f) => ({
        planet: f.planet,
        house: f.house ? parseInt(f.house, 10) : null,
        sign: f.sign || null,
      }));
    if (!asc && active.length === 0) {
      toast.error("Set an ascendant or at least one planet filter.");
      return;
    }
    setBusy(true);
    // Stream the results — each frame ticks the progress bar 1% → 100%
    // as the backend walks the catalog. We use raw fetch() here instead
    // of axios because axios doesn't expose a ReadableStream on the
    // browser response.
    const matches = [];
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${backend}/api/celebrities/lookalike/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ascendant: asc || null,
          planet_filters: active,
        }),
      });
      if (!resp.ok) {
        // Server returned a validation error before the stream started —
        // read the body as JSON and surface the detail. 401 = members-only
        // celebrity database (sign-in required since Oct 2026).
        let msg = `HTTP ${resp.status}`;
        if (resp.status === 401) {
          msg = "Please sign in to search the celebrity database.";
        } else {
          try {
            const err = await resp.json();
            msg = err?.detail || msg;
          } catch { /* body was already consumed or wasn't JSON */ }
        }
        throw new Error(msg);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let final = { count: 0, warming: 0 };
      let sawAnyFrame = false;
      // Watchdog — if the proxy is buffering (some corporate proxies +
      // certain Cloudflare configs) and no frame arrives for 20s, we
      // abort the stream and fall back to the non-streaming endpoint
      // below so the user still gets an answer instead of the 120s
      // origin-timeout error page.
      const watchdog = setTimeout(() => {
        if (!sawAnyFrame) reader.cancel().catch(() => {});
      }, 20000);
      try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          sawAnyFrame = true;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";
          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            try {
              const evt = JSON.parse(trimmed);
              if (evt.type === "progress") {
                setProgress({ done: evt.done, total: evt.total, current: evt.current });
              } else if (evt.type === "match") {
                matches.push(evt.match);
                setResult({ count: matches.length, warming: 0, matches: [...matches] });
              } else if (evt.type === "done") {
                final = {
                  count: evt.count,
                  warming: evt.warming,
                  unlocked: !!evt.unlocked,
                  locked_count: evt.locked_count || 0,
                };
              }
            } catch { /* skip malformed frame */ }
          }
        }
      } finally {
        clearTimeout(watchdog);
      }
      if (!sawAnyFrame) {
        // Stream was blocked by proxy buffering — fall back to the
        // non-streaming endpoint so the user still gets a result.
        const { data } = await api.post("/celebrities/lookalike", {
          ascendant: asc || null,
          planet_filters: active,
        });
        setResult(data);
        if ((data?.count || 0) === 0) toast.info("No celebrities match this exact combination.");
        return;
      }
      setResult({ count: final.count, warming: final.warming, matches, unlocked: final.unlocked, locked_count: final.locked_count });
      if (final.count === 0) toast.info("No celebrities match this exact combination.");
    } catch (err) {
      const msg = err?.message || "Search failed.";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell>
      <div className="max-w-5xl mx-auto px-4 py-6" data-testid="celeb-lookalike-page">
        {/* Hero */}
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 text-amber-600 text-xs font-semibold uppercase tracking-wide">
            <Sparkles size={14} /> Astro Research
          </div>
          <h1 className="text-3xl sm:text-4xl font-serif font-bold text-slate-900 mt-2">
            Celebrity Lookalike Calculator
          </h1>
          <p className="text-slate-500 mt-2 max-w-2xl">
            Pick an ascendant, add any planet + house + sign combos you're curious
            about, and this tool scans our entire celebrity catalog for exact matches.
            Skip any planet you don't care about — the search AND-s only what you fill in.
          </p>
        </div>

        {/* Query form */}
        <section className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-slate-700 text-sm font-medium">Ascendant (Lagna)</span>
              <select
                value={asc}
                onChange={(e) => setAsc(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:border-blue-500 text-sm bg-white"
                data-testid="lookalike-asc"
              >
                <option value="">— Any —</option>
                {ZODIAC_SIGNS.map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
            </label>
          </div>

          <div className="mt-5 border-t border-slate-100 pt-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-800 font-semibold text-sm">Planet filters</p>
              <button
                onClick={addFilter}
                disabled={filters.length >= 9}
                className="text-blue-600 hover:text-blue-800 disabled:opacity-40 text-sm inline-flex items-center gap-1 font-medium"
                data-testid="lookalike-add-filter-btn"
              >
                <Plus size={14} /> Add planet
              </button>
            </div>
            <div className="space-y-2">
              {filters.map((f, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`lookalike-filter-row-${i}`}>
                  <div className="col-span-4">
                    <label className="text-slate-500 text-[11px] font-medium uppercase tracking-wide">Planet</label>
                    <select
                      value={f.planet}
                      onChange={(e) => updateFilter(i, { planet: e.target.value })}
                      className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-sm bg-white"
                      data-testid={`lookalike-planet-${i}`}
                    >
                      <option value="">Select…</option>
                      {PLANETS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="col-span-3">
                    <label className="text-slate-500 text-[11px] font-medium uppercase tracking-wide">House</label>
                    <select
                      value={f.house}
                      onChange={(e) => updateFilter(i, { house: e.target.value })}
                      className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-sm bg-white"
                      data-testid={`lookalike-house-${i}`}
                    >
                      <option value="">Any</option>
                      {HOUSES.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                  <div className="col-span-4">
                    <label className="text-slate-500 text-[11px] font-medium uppercase tracking-wide">Sign</label>
                    <select
                      value={f.sign}
                      onChange={(e) => updateFilter(i, { sign: e.target.value })}
                      className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-sm bg-white"
                      data-testid={`lookalike-sign-${i}`}
                    >
                      <option value="">Any</option>
                      {ZODIAC_SIGNS.map((z) => <option key={z} value={z}>{z}</option>)}
                    </select>
                  </div>
                  <div className="col-span-1 pb-1">
                    {filters.length > 1 && (
                      <button
                        onClick={() => removeFilter(i)}
                        className="text-slate-400 hover:text-rose-600"
                        data-testid={`lookalike-remove-filter-${i}`}
                        title="Remove"
                      >
                        <XIcon size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-slate-400 text-xs mt-3">
              Empty House or Sign = wildcard. So "Saturn · Any · Aquarius" matches Saturn in Aquarius
              anywhere in the chart.
            </p>
          </div>

          <div className="mt-5 flex items-center gap-3">
            <button
              onClick={runSearch}
              disabled={busy}
              className="btn-primary rounded-full px-6 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="lookalike-search-btn"
            >
              <Search size={14} /> {busy ? "Searching…" : "Find lookalikes"}
            </button>
            {(result || asc || filters.some(f => f.planet)) && !busy && (
              <button
                onClick={() => {
                  setAsc("");
                  setFilters([{ planet: "", house: "", sign: "" }]);
                  setResult(null);
                  setProgress(null);
                  setError("");
                  try { sessionStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
                }}
                className="text-slate-500 hover:text-slate-800 text-sm underline underline-offset-2"
                data-testid="lookalike-clear-btn"
              >
                Clear
              </button>
            )}
            {error && <span className="text-rose-600 text-sm">{error}</span>}
          </div>

          {/* Live progress bar — driven by NDJSON progress frames arriving
              from the streaming endpoint. Fills 0% → 100% as the backend
              walks the catalog, showing the current celebrity name so it
              never feels like a fake spinner. Stays visible for a beat
              after completion so the admin sees the 100% checkpoint. */}
          {progress && (
            <div className="mt-4" data-testid="lookalike-progress">
              <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                <span className="font-medium truncate pr-3">
                  {busy
                    ? `Scanning ${progress.done} of ${progress.total}${progress.current ? ` — ${progress.current}` : ""}`
                    : `Scanned ${progress.total}/${progress.total}`}
                </span>
                <span className="font-mono font-semibold text-slate-800">
                  {progress.total > 0
                    ? Math.round((progress.done / progress.total) * 100)
                    : 0}%
                </span>
              </div>
              <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-150 ${busy ? "bg-blue-500" : "bg-emerald-600"}`}
                  style={{
                    width: `${progress.total > 0 ? (progress.done / progress.total) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>
          )}
        </section>

        {/* Results */}
        {result && (
          <section className="mt-8" data-testid="lookalike-results">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-xl font-semibold text-slate-900">
                {result.count} match{result.count === 1 ? "" : "es"}
              </h2>
              {result.warming > 0 && (
                <span className="text-xs text-amber-600">
                  {result.warming} celebrities still indexing — retry in a minute.
                </span>
              )}
            </div>
            {result.count === 0 ? (
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 text-center">
                <p className="text-slate-600">
                  No celebrities in our catalog match this exact combination. Try loosening one filter.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {result.matches.map((m) => (
                  <Link
                    key={m.slug}
                    to={`/celebrities/detail/${m.slug}`}
                    className="bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-md hover:border-blue-300 transition group"
                    data-testid={`lookalike-match-${m.slug}`}
                  >
                    <div className="flex items-center gap-3">
                      {m.photo ? (
                        <img
                          src={m.photo}
                          alt={m.name}
                          className="w-14 h-14 rounded-full object-cover border border-slate-200"
                          referrerPolicy="no-referrer"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 font-semibold">
                          {(m.name || "?").slice(0, 1)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 truncate group-hover:text-blue-700">{m.name}</p>
                        <p className="text-slate-500 text-xs truncate">{m.industry || m.category || ""}</p>
                        <p className="text-slate-400 text-xs truncate">{m.dob} · {m.country || ""}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Paywalled remainder — the backend never sends locked match
                data, so these are pure skeleton placeholders (nothing hidden
                to un-blur) + the unlock card keyed on the query fingerprint. */}
            {result.count > 0 && !result.unlocked && (result.locked_count || 0) > 0 && (
              <div className="mt-4" data-testid="lookalike-locked-section">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Array.from({ length: Math.min(result.locked_count, 6) }).map((_, i) => (
                    <div
                      key={i}
                      className="relative bg-white border border-slate-200 rounded-2xl p-4 overflow-hidden select-none"
                      data-testid={`lookalike-locked-${i}`}
                    >
                      <div className="flex items-center gap-3 blur-[6px] opacity-80" aria-hidden="true">
                        <div className="w-14 h-14 rounded-full bg-slate-200" />
                        <div className="flex-1 space-y-2">
                          <div className="h-3.5 w-3/4 bg-slate-300 rounded" />
                          <div className="h-2.5 w-1/2 bg-slate-200 rounded" />
                          <div className="h-2.5 w-2/3 bg-slate-200 rounded" />
                        </div>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Lock size={18} className="text-slate-400" />
                      </div>
                    </div>
                  ))}
                  {result.locked_count > 6 && (
                    <div className="rounded-2xl border border-dashed border-slate-300 p-4 flex items-center justify-center text-slate-500 text-sm" data-testid="lookalike-locked-more">
                      +{result.locked_count - 6} more matches
                    </div>
                  )}
                </div>
                <PaywallGate
                  tool="celebrity_lookalike"
                  toolLabel="Celebrity Lookalike match list"
                  birthDetails={queryBirth}
                  variant="standalone"
                  forWhat="for this search"
                  pitch={`Unlock all ${result.count} celebrity matches for this exact search — permanently, across all your devices.`}
                  onUnlocked={() => { if (result) fetchFullResults(); }}
                  className="mt-4"
                />
              </div>
            )}
          </section>
        )}

        <div className="mt-10" data-testid="lookalike-twenty-year-promo">
          <PremiumTeaser
            testid="lookalike-premium-teaser"
            topLine={
              result?.matches?.length ? (
                <>
                  Your chart echoes {result.matches[0].name}&apos;s. But your story is your own.
                  <br />
                  <span className="text-amber-300">Unlock 55+ pages of what&apos;s next for you.</span>
                </>
              ) : (
                <>
                  Celebrity twins are fun. Your own 20-year story is life-changing.
                  <br />
                  <span className="text-amber-300">Unlock 55+ pages of what&apos;s next.</span>
                </>
              )
            }
            subLine={
              <>
                The same placements that shaped famous lives are timed by <i>your</i> dashas. The full 20-year
                Vedic report decodes your Sade Sati window, 6 doshas, gem &amp; rudraksha recommendations, and
                year-by-year career, marriage, wealth and health scores.
              </>
            }
          />
        </div>
      </div>
    </PageShell>
  );
}
